import {
  AccountJoinOrganizationPutRequest,
  LoginResponse,
  OrganizationAuthResponse,
} from 'api/core';
import { useJoinOrganization } from 'api/useAuthApi';
import { EntitySelectSingle } from 'components/Select/EntitySelectSingle';
import { useMemo } from 'react';
import { useForm } from 'react-hook-form';

interface JoinOrganizationFormProps {
  user: LoginResponse;
  allOrganizations: OrganizationAuthResponse[];
  onSuccess: () => void;
  onCancel: () => void;
  inDialog?: boolean;
}

export const JoinOrganizationForm = ({
  user,
  allOrganizations,
  onSuccess,
  onCancel,
  inDialog,
}: JoinOrganizationFormProps) => {
  const { mutateAsync, isPending } = useJoinOrganization();

  const sortedOrganizations = useMemo(
    () => sortOrganizations(allOrganizations),
    [allOrganizations]
  );

  const { register, handleSubmit, setValue, watch } =
    useForm<AccountJoinOrganizationPutRequest>({
      defaultValues: {
        organizationId: user.organization.id,
      },
    });

  const onSubmit = handleSubmit(async (result) => {
    await mutateAsync({
      organizationId: result.organizationId,
    });
    onSuccess();
  });

  const onOrganizationSelected = (
    organization: OrganizationAuthResponse | null
  ) => {
    console.log('Selected organization:', organization);
    if (!organization) return;
    setValue('organizationId', organization.id);
  };

  return (
    <form onSubmit={onSubmit} className="space-y-2">
      <div className="form-control">
        <label className="label">
          <span className="label-text">Butik</span>
        </label>
        <EntitySelectSingle<OrganizationAuthResponse>
          data={sortedOrganizations}
          renderFormat={(format) => (
            <span
              className={format.id == user.organization.id ? 'font-bold' : ''}
            >
              {format.name}
            </span>
          )}
          onSelect={onOrganizationSelected}
          initialValue={sortedOrganizations.find(
            (org) => org.id === user.organization.id
          )}
          searchPropertyKey="name"
          inDialog={inDialog}
        />
        <input
          {...register('organizationId', { required: true })}
          type="text"
          className="hidden"
        />
        <div className="label">
          <span className="label-text-alt">
            Du kan ændre din butik her, hvis du arbejder på vegne af en anden
            butik.
          </span>
        </div>
      </div>

      <div className="flex justify-center space-x-4 pt-4">
        <button
          className="btn btn-primary"
          disabled={
            isPending || watch('organizationId') == user.organization.id
          }
        >
          Skift
        </button>
        {onCancel ? (
          <button type="button" className="btn" onClick={onCancel}>
            Annuller
          </button>
        ) : null}
      </div>
    </form>
  );
};

// Should sort organizations by name, but also by if the parental hierarchy.
// So first the first organization without a parent, then its children (and those children and so on),
// then the next organization without a parent, and so on.
// Moreover, we also modify the name to prepend a space for each level of hierarchy.
// This way, the organizations are displayed in a tree-like structure.
const sortOrganizations = (
  organizations: OrganizationAuthResponse[]
): OrganizationAuthResponse[] => {
  const knownOrganizations = new Set(organizations.map((org) => org.id));

  const sorted: OrganizationAuthResponse[] = [];
  const visited: Set<string> = new Set();

  const addOrganization = (org: OrganizationAuthResponse, depth = 0) => {
    if (visited.has(org.id)) return;
    visited.add(org.id);

    // Create a new organization object with modified name based on hierarchy depth
    const modifiedOrg = {
      ...org,
      name: '‎ㅤ'.repeat(depth) + ' ' + org.name,
    };

    sorted.push(modifiedOrg);

    organizations
      .filter((child) => child.parentOrganizationId === org.id)
      .sort((a, b) => a.name.localeCompare(b.name))
      .forEach((child) => addOrganization(child, depth + 1));
  };

  organizations
    .filter(
      (org) =>
        !org.parentOrganizationId ||
        !knownOrganizations.has(org.parentOrganizationId)
    )
    .sort((a, b) => a.name.localeCompare(b.name))
    .forEach((org) => {
      addOrganization(org, 0);
    });

  return sorted;
};
